import { useMemo, useState } from "react";
import { useNavigate } from "../../lib/i18n";
import { motion, AnimatePresence } from "framer-motion";
import { Info, ArrowRight, Truck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "../ui/slider";
import { api, fmt, errMsg } from "../../lib/api";
import { useCart } from "../../context/CartContext";

const Choice = ({ label, options, value, onChange, id }) => (
  <div>
    <p className="label">{label}</p>
    <div className="flex flex-wrap gap-2">
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`calc-${id}-${v}`}
          className={`rounded-md border px-3.5 py-2 text-xs font-medium transition-colors ${value === v ? "border-brun bg-brun text-ivoire" : "border-beige bg-white text-encre hover:border-bois"}`}>{l}</button>
      ))}
    </div>
  </div>
);

const INS = { faible: 180, moyenne: 120, bonne: 75, excellente: 45 };
const HOUSE = { maison: 1, mitoyenne: 0.85, appartement: 0.7, chalet: 1.1 };
const HEAT = { principal: 1, appoint: 0.4, agrement: 0.15 };
const REGION = { ouest: 0.9, centre: 1, est: 1.1, montagne: 1.25 };
const FREQ = { quotidienne: 1, reguliere: 0.7, occasionnelle: 0.35 };

export function WoodCalculator({ compact = false }) {
  const [s, setS] = useState({ surface: 120, house: "maison", insulation: "moyenne", heating: "principal", days: 180, region: "centre", freq: "quotidienne" });
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);
  const { add } = useCart();
  const nav = useNavigate();
  const set = (k) => (v) => { setS((o) => ({ ...o, [k]: v })); setShown(false); };

  const stere = useMemo(() => {
    const kwh = s.surface * INS[s.insulation] * HOUSE[s.house] * HEAT[s.heating] * REGION[s.region] * (s.days / 200) * FREQ[s.freq];
    return Math.max(0.5, Math.round((kwh / 1600) * 2) / 2);
  }, [s]);

  const order = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/products", { params: { category: "bois-de-chauffage", limit: 10 } });
      const p = data.find((x) => x.sku === "BOI-MIX-33") || data[0];
      add(p, Math.ceil(stere), "33 cm");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className={`grid gap-10 ${compact ? "" : "lg:grid-cols-12"}`} data-testid="wood-calculator">
      <div className={`space-y-7 ${compact ? "" : "lg:col-span-7"}`}>
        <div>
          <div className="flex justify-between"><p className="label">Surface du logement</p><span className="font-serif text-xl text-brun" data-testid="calc-surface-value">{s.surface} m²</span></div>
          <Slider value={[s.surface]} min={30} max={400} step={5} onValueChange={([v]) => set("surface")(v)} data-testid="calc-surface-slider" />
        </div>
        <Choice id="house" label="Type d'habitation" value={s.house} onChange={set("house")} options={[["maison", "Maison individuelle"], ["mitoyenne", "Maison mitoyenne"], ["appartement", "Appartement"], ["chalet", "Chalet / résidence"]]} />
        <Choice id="insulation" label="Niveau d'isolation" value={s.insulation} onChange={set("insulation")} options={[["faible", "Faible"], ["moyenne", "Moyenne"], ["bonne", "Bonne"], ["excellente", "Très performante"]]} />
        <Choice id="heating" label="Type de chauffage au bois" value={s.heating} onChange={set("heating")} options={[["principal", "Chauffage principal"], ["appoint", "Appoint"], ["agrement", "Flambées d'agrément"]]} />
        <div>
          <div className="flex justify-between"><p className="label">Nombre de jours d'utilisation par an</p><span className="font-serif text-xl text-brun">{s.days} j</span></div>
          <Slider value={[s.days]} min={20} max={300} step={10} onValueChange={([v]) => set("days")(v)} data-testid="calc-days-slider" />
        </div>
        <Choice id="region" label="Région" value={s.region} onChange={set("region")} options={[["ouest", "Ouest / Baltique"], ["centre", "Centre"], ["est", "Est"], ["montagne", "Montagne (Tatras, Sudètes)"]]} />
        <Choice id="freq" label="Fréquence d'utilisation" value={s.freq} onChange={set("freq")} options={[["quotidienne", "Tous les jours"], ["reguliere", "Plusieurs fois / semaine"], ["occasionnelle", "Occasionnelle"]]} />
        {!shown && <button onClick={() => setShown(true)} className="btn-primary" data-testid="wood-calculator-submit-btn">Calculer mon besoin <ArrowRight size={15} /></button>}
      </div>
      <div className={compact ? "" : "lg:col-span-5"}>
        <div className="sticky top-32 rounded-lg bg-brun p-8 md:p-10 text-ivoire grain relative overflow-hidden">
          <p className="eyebrow !text-laiton">Votre estimation</p>
          <AnimatePresence mode="wait">
            <motion.div key={shown ? stere : "x"} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              {shown ? (
                <>
                  <p className="mt-4 font-serif text-2xl !text-ivoire leading-snug">Nous estimons votre besoin annuel à environ</p>
                  <p className="mt-2 font-serif text-7xl font-semibold text-laiton" data-testid="calc-result">{stere.toLocaleString("fr-FR")} <span className="text-3xl">stères</span></p>
                  <p className="mt-2 text-sm text-ivoire/60">soit ≈ {Math.round(stere * 0.8 * 10) / 10} m³ de bûches en 33 cm</p>
                  <button onClick={order} disabled={busy} className="btn-accent mt-8 w-full" data-testid="calc-order-recommended-btn">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : null}Commander la quantité recommandée
                  </button>
                  <button onClick={() => nav("/devis")} className="btn-light mt-3 w-full">Demander conseil</button>
                </>
              ) : <p className="mt-4 font-serif text-2xl !text-ivoire/80">Renseignez votre logement, puis lancez le calcul.</p>}
            </motion.div>
          </AnimatePresence>
          <p className="mt-8 flex gap-2 border-t border-ivoire/15 pt-5 text-xs leading-relaxed text-ivoire/60"><Info size={14} className="shrink-0 mt-0.5" />Il s'agit d'une estimation indicative basée sur des moyennes. Votre consommation réelle dépend de votre appareil, de vos habitudes et de l'hiver.</p>
        </div>
      </div>
    </div>
  );
}

export function DeliveryCalculator({ defaultVolume = 3 }) {
  const { postal, setPostal } = useCart();
  const [cp, setCp] = useState(postal || "");
  const [vol, setVol] = useState(defaultVolume);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/delivery/estimate", { postal_code: cp, volume: vol }); setRes(data); setPostal(cp); }
    catch (er) { setRes(null); setErr(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <div className="card-soft p-6 md:p-8" data-testid="delivery-calculator">
      <form onSubmit={run} className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <div><label className="label">Code postal</label><input value={cp} onChange={(e) => setCp(e.target.value)} placeholder="ex. 50-001" required className="field" data-testid="delivery-postal-input" /></div>
        <div><label className="label">Quantité (stères)</label><input type="number" min={0} step={0.5} value={vol} onChange={(e) => setVol(parseFloat(e.target.value) || 0)} className="field" data-testid="delivery-volume-input" /></div>
        <button className="btn-primary" disabled={busy} data-testid="delivery-calc-submit">{busy ? <Loader2 size={15} className="animate-spin" /> : <Truck size={15} />}Calculer</button>
      </form>
      {err && <p className="mt-4 text-sm text-[#9B2C2C]" data-testid="delivery-calc-error">{err}</p>}
      {res && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {res.map((r) => (
            <div key={r.tier} className="rounded-md border border-beige bg-creme/50 p-4" data-testid={`delivery-tier-${r.tier}`}>
              <div className="flex items-baseline justify-between"><p className="font-medium text-brun">{r.tier_name}</p><p className="font-serif text-2xl text-brun">{r.fee === 0 ? "Offerte" : fmt(r.fee)}</p></div>
              <p className="mt-1 text-xs text-pierre">{r.zone} · {r.truck} · dès le {new Date(r.estimated_date).toLocaleDateString("fr-FR")}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
